import { hasPermission, useUser } from '@hakimo-ui/hakimo/util';
import {
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  Cog6ToothIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { NavLink, useNavigate } from 'react-router-dom';
import HakimoNavLink from '../hakimo/HakimoNavLink';
import Links from './Links';
import ProfilePopover from './ProfilePopover';
import { NavItem } from './types';

interface Props {
  collapsed?: boolean;
  navItems: NavItem[];
  toggleSidebar: () => void;
  onClick?: () => void;
}

function SidebarContent(props: Props) {
  const { collapsed, navItems, onClick = () => null, toggleSidebar } = props;
  const user = useUser();
  const navigate = useNavigate();

  const items = navItems.filter((item) =>
    item.requiredPermissions.every((perm) => hasPermission(user, perm))
  );

  const onClickSettings = () => {
    navigate('/profile/general');
  };

  return (
    <div
      className={clsx('flex h-full flex-col px-2', collapsed && 'text-center')}
    >
      <div className="flex flex-1 flex-col gap-4 whitespace-nowrap py-2.5">
        {collapsed && <HakimoNavLink short />}
        {!collapsed && <HakimoNavLink />}
        <Links items={items} short={collapsed} onClick={onClick} />
      </div>
      <div className="relative mt-auto flex flex-col items-center pb-4">
        <div className={clsx('mb-1', !collapsed && 'w-full self-start')}>
          <NavLink
            to="/profile"
            className={({ isActive }) =>
              clsx(
                isActive
                  ? 'dark:text-dark-text bg-gray-200 text-gray-700 dark:bg-gray-700'
                  : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300',
                'flex items-center space-x-2 rounded-md px-3 py-2'
              )
            }
            onClick={onClickSettings}
          >
            {({ isActive }) => (
              <>
                <Cog6ToothIcon
                  className={clsx(
                    isActive
                      ? 'dark:text-dark-text text-gray-700'
                      : 'text-gray-500 group-hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300',
                    collapsed && isActive && 'bg-gray-200 dark:bg-gray-700',
                    'h-6 w-6 flex-shrink-0'
                  )}
                />

                {!collapsed && <div className="text-sm">Settings</div>}
              </>
            )}
          </NavLink>
        </div>

        <ProfilePopover collapsed={collapsed} />

        <div
          className={clsx(
            'absolute',
            collapsed
              ? 'bottom-2 left-1/2 -translate-x-1/2'
              : 'bottom-2 right-2'
          )}
        >
          <button
            onClick={toggleSidebar}
            className="text-gray-400 transition hover:text-black dark:hover:text-white"
          >
            {collapsed ? (
              <ChevronDoubleRightIcon className="h-5 w-5" />
            ) : (
              <ChevronDoubleLeftIcon className="h-5 w-5" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default SidebarContent;
